import { isSignalNode, type CsSubgraph } from '@bloo-team/api-client'
import {
  isPromoted, KILL_CHAIN, readSeverity, SEVERITY_ORDER, severityForScore, stageForTactic,
  type KillChainStage, type PromotionRule, type Signal, type SignalEntity, type SignalSeverity,
} from './signal-model'

/**
 * Storylines = connected-signals subgraphs (docs/signals.md §Storylines).
 * The job (live) or the port (demo, lib/connected-signals.ts) returns raw
 * subgraphs; this is the one door that turns them into the page's shape.
 * The UI says "storyline"; the code says "subgraph".
 */

/** Legacy `graph_data.slice(0, 50)` — server order, BEFORE sorting. */
export const SUBGRAPH_CAP = 50

export interface SubgraphSignal {
  /** Kept uuid (the merge_ids key). */
  uuid: string
  /** Merged siblings — merge_ids[uuid] without the kept one. */
  folded: string[]
  count: number
  detectionName: string
  tactic: string
  technique: string
  severity: SignalSeverity
  weight: number | null
  /** starttime / endtime, else CNAMTime; null when the node carries none. */
  start: number | null
  end: number | null
}

export interface SubgraphEntity {
  /** Canvas node id (entityNodeId). */
  id: string
  value: string
  field: string
  role: 'suspect' | 'target' | 'compromised'
}

export interface Subgraph {
  /** Smallest uuid over ALL merge_ids members — stable across re-runs (`?sg=`). */
  key: string
  /** subgraph_name — TEXT. title_html is never read. */
  title: string
  entityName: string | null
  /** total_weight. */
  score: number
  tactics: string[]
  /** Distinct techniques over the kept signals, first-seen order. */
  techniques: string[]
  severity: SignalSeverity
  /** Earliest start / latest end over the kept signals; null when none has a time. */
  first: number | null
  last: number | null
  signals: SubgraphSignal[]
  entities: SubgraphEntity[]
  links: { from: string; to: string }[]
  uuids: string[]
  counts: { signals: number; suspects: number; targets: number; compromised: number }
  /** The subgraph as the job returned it — the attribution request sends it back. */
  raw: CsSubgraph
}

export type PromotedSubgraph = Subgraph & { promoted: boolean; watched: boolean }

/** Storyline panel tabs, in order (`?stab=`; absent = signals). */
export const STORY_TABS = ['signals', 'artifacts', 'campaigns', 'mitre'] as const
export type StoryTab = (typeof STORY_TABS)[number]
export const isStoryTab = (v: unknown): v is StoryTab => typeof v === 'string' && (STORY_TABS as readonly string[]).includes(v)

export const signalNodeId = (uuid: string): string => `sig:${uuid}`
export const entityNodeId = (id: string | number): string => `ent:${typeof id === 'number' ? 'n' : 's'}:${id}`

const num = (v: unknown): number | null => {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v)
  return null
}

export function readSubgraph(raw: CsSubgraph): Subgraph {
  const sigNodes = raw.nodes.filter(isSignalNode)
  const sigIds = new Set(sigNodes.map((n) => n.id))
  const signals: SubgraphSignal[] = sigNodes.map((n) => {
    const weight = num(n.weight)
    const members = raw.merge_ids[n.id] ?? [n.id]
    return {
      uuid: n.id,
      folded: members.filter((u) => u !== n.id),
      count: n.count,
      detectionName: n.DetectionName ?? '',
      tactic: n.DetectionTactic ?? '',
      technique: n.DetectionTechnique ?? '',
      severity: readSeverity(n.DetectionSeverity) ?? (weight !== null ? severityForScore(weight) : 'info'),
      weight,
      start: n.starttime ?? n.CNAMTime ?? null,
      end: n.endtime ?? n.CNAMTime ?? null,
    }
  })
  const entities: SubgraphEntity[] = raw.nodes.flatMap((n) =>
    isSignalNode(n) ? [] : [{ id: entityNodeId(n.id), value: String(n.id), field: n.field, role: n.group }],
  )
  const end = (v: string | number) => (typeof v === 'string' && sigIds.has(v) ? signalNodeId(v) : entityNodeId(v))
  // tolerantArray may have dropped a node; a link to it would dangle on the canvas.
  const known = new Set([...signals.map((s) => signalNodeId(s.uuid)), ...entities.map((e) => e.id)])
  const links = raw.links.map((l) => ({ from: end(l.source), to: end(l.target) })).filter((l) => known.has(l.from) && known.has(l.to))
  const fromMerge = [...new Set(Object.values(raw.merge_ids).flat())]
  const uuids = fromMerge.length ? fromMerge : signals.map((s) => s.uuid)
  const tactics: string[] = []
  for (const s of signals) if (s.tactic && !tactics.includes(s.tactic)) tactics.push(s.tactic)
  const techniques: string[] = []
  for (const s of signals) if (s.technique && !techniques.includes(s.technique)) techniques.push(s.technique)
  let severity: SignalSeverity = 'info'
  for (const s of signals) if (SEVERITY_ORDER.indexOf(s.severity) < SEVERITY_ORDER.indexOf(severity)) severity = s.severity
  const starts = signals.flatMap((s) => (s.start === null ? [] : [s.start]))
  const ends = signals.flatMap((s) => (s.end === null ? [] : [s.end]))
  return {
    key: [...uuids].sort()[0] ?? '',
    title: raw.subgraph_name,
    entityName: raw.entity_name,
    score: raw.total_weight,
    tactics,
    techniques,
    severity,
    first: starts.length ? Math.min(...starts) : null,
    last: ends.length ? Math.max(...ends) : null,
    signals,
    entities,
    links,
    uuids,
    counts: { signals: raw.no_of_signals, suspects: raw.no_of_suspects, targets: raw.no_of_targets, compromised: raw.no_of_compromised },
    raw,
  }
}

export function readSubgraphs(raw: CsSubgraph[]): { subgraphs: Subgraph[]; total: number } {
  return { subgraphs: raw.slice(0, SUBGRAPH_CAP).map(readSubgraph), total: raw.length }
}

/** The VALUE half of each watched entityKey (`field:value`), lowercased (decision S35). */
export function watchedValues(watchlist: readonly string[]): Set<string> {
  return new Set(watchlist.map((k) => k.slice(k.indexOf(':') + 1).toLowerCase()))
}

/** Watched when any entity's value matches — field ignored, as the worker keys entities by value. */
export function isWatched(sg: Pick<Subgraph, 'entities'>, values: ReadonlySet<string>): boolean {
  return values.size > 0 && sg.entities.some((e) => values.has(e.value.toLowerCase()))
}

/** Promote + watch + sort: watched, promoted, score desc, last desc (stable). */
export function rankSubgraphs(list: Subgraph[], rule: PromotionRule, watched: ReadonlySet<string>): PromotedSubgraph[] {
  return list
    .map((sg) => ({ ...sg, promoted: isPromoted(sg, rule), watched: isWatched(sg, watched) }))
    .sort(
      (a, b) =>
        Number(b.watched) - Number(a.watched) ||
        Number(b.promoted) - Number(a.promoted) ||
        b.score - a.score ||
        (b.last ?? 0) - (a.last ?? 0),
    )
}

export function subgraphFor(uuid: string, list: readonly PromotedSubgraph[]): PromotedSubgraph | null {
  return list.find((sg) => sg.uuids.includes(uuid)) ?? null
}

export interface KillChainColumn {
  stage: KillChainStage | 'unmapped'
  signals: SubgraphSignal[]
}

export function killChainColumns(sg: Pick<Subgraph, 'signals'>): KillChainColumn[] {
  const cols: KillChainColumn[] = [...KILL_CHAIN.map((stage) => ({ stage, signals: [] as SubgraphSignal[] })), { stage: 'unmapped', signals: [] }]
  for (const s of [...sg.signals].sort((a, b) => (a.start ?? 0) - (b.start ?? 0))) {
    const stage = stageForTactic(s.tactic) ?? 'unmapped'
    cols.find((c) => c.stage === stage)!.signals.push(s)
  }
  return cols
}

/* ------------------------------------------------- storyline drawer rows */

export interface StorylineRow {
  uuid: string
  name: string
  /** ms epoch; null for a merged sibling that is not loaded. */
  at: number | null
  severity: SignalSeverity
  /** Targets first, then suspects. */
  entities: SignalEntity[]
}

const capitalise = (f: string) => (f ? f.charAt(0).toUpperCase() + f.slice(1) : 'Entity')

/**
 * The storyline drawer's Signals tab: the job's merges undone, so every
 * member uuid is its own row, newest first (unknown times last). A loaded
 * row speaks for itself. An unloaded kept signal reads its job node and the
 * entities linked to it on the canvas; an unloaded merged sibling borrows
 * its kept signal's name and severity, with no time and no entities (the
 * job sends a sibling's uuid alone).
 */
export function storylineRows(st: Pick<Subgraph, 'signals' | 'entities' | 'links'>, resolve: (uuid: string) => Signal | null): StorylineRow[] {
  const byId = new Map(st.entities.map((e) => [e.id, e]))
  const linked = (uuid: string): SignalEntity[] => {
    const node = signalNodeId(uuid)
    const out: SignalEntity[] = []
    for (const l of st.links) {
      const other = l.from === node ? l.to : l.to === node ? l.from : null
      const e = other ? byId.get(other) : undefined
      if (e && !out.some((x) => x.value === e.value && x.field === capitalise(e.field)))
        out.push({ role: e.role === 'suspect' ? 'Suspect' : 'Target', field: capitalise(e.field), value: e.value })
    }
    return out
  }
  const targetsFirst = (es: SignalEntity[]) => [...es.filter((e) => e.role === 'Target'), ...es.filter((e) => e.role === 'Suspect')]
  const seen = new Set<string>()
  const rows: StorylineRow[] = []
  for (const k of st.signals) {
    for (const uuid of [k.uuid, ...k.folded]) {
      if (seen.has(uuid)) continue
      seen.add(uuid)
      const r = resolve(uuid)
      if (r) rows.push({ uuid, name: r.detectionName || k.detectionName, at: r.at, severity: r.severity, entities: targetsFirst(r.entities) })
      else if (uuid === k.uuid) rows.push({ uuid, name: k.detectionName, at: k.end, severity: k.severity, entities: targetsFirst(linked(uuid)) })
      else rows.push({ uuid, name: k.detectionName, at: null, severity: k.severity, entities: [] })
    }
  }
  return rows.sort((a, b) => (a.at === null ? (b.at === null ? 0 : 1) : b.at === null ? -1 : b.at - a.at))
}
